import React from 'react';
import { format, isSameDay } from 'date-fns';
import {
  WEEKDAY_HEADERS,
  appointmentsForDay,
  buildMonthGrid,
  formatShortTime,
  isOutsideMonth,
  isToday,
  leadDisplayName,
  statusColor,
} from '../../lib/calendarUtils';

function AppointmentPill({ appt, onClick }) {
  const color = statusColor(appt.status);
  const time = formatShortTime(appt.scheduled_at);
  const name = leadDisplayName(appt);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(appt);
      }}
      className="w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate text-white hover:opacity-90"
      style={{ backgroundColor: color }}
      title={`${time} ${name}`}
    >
      <span className="font-medium">{time}</span> {name}
    </button>
  );
}

export default function CalendarMonthView({
  focusDate,
  selectedDate,
  appointments,
  onSelectDay,
  onSelectAppointment,
  onExpandDay,
}) {
  const cells = buildMonthGrid(focusDate);

  return (
    <div className="flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-[28rem]">
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {WEEKDAY_HEADERS.map((d) => (
          <div key={d} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 flex-1 auto-rows-fr min-h-[24rem]">
        {cells.map((day) => {
          const outside = isOutsideMonth(day, focusDate);
          const selected = selectedDate && isSameDay(day, selectedDate);
          const today = isToday(day);
          const dayAppts = appointmentsForDay(appointments, day);
          const visible = dayAppts.slice(0, 3);
          const more = dayAppts.length - visible.length;

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onSelectDay(day)}
              className={`min-h-[88px] border-b border-r border-gray-100 p-1.5 text-left flex flex-col gap-0.5 transition-colors hover:bg-blue-50/40 ${
                selected ? 'bg-blue-50 ring-1 ring-inset ring-primary/30' : ''
              } ${outside ? 'bg-gray-50/80' : 'bg-white'}`}
            >
              <span
                className={`inline-flex items-center justify-center w-7 h-7 text-sm font-medium rounded-full shrink-0 ${
                  today
                    ? 'bg-[#00AAFF] text-white'
                    : outside
                      ? 'text-gray-400'
                      : 'text-gray-800'
                }`}
              >
                {format(day, 'd')}
              </span>
              <div className="flex-1 flex flex-col gap-0.5 overflow-hidden min-h-0">
                {visible.map((appt) => (
                  <AppointmentPill key={appt.id} appt={appt} onClick={onSelectAppointment} />
                ))}
                {more > 0 && (
                  <button
                    type="button"
                    className="text-[10px] font-medium text-primary hover:underline text-left px-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpandDay(day);
                    }}
                  >
                    +{more} more
                  </button>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
